import React, { useState, useCallback, useEffect } from 'react';
import {
  KioskLayout,
  KioskAuthGate,
  KioskModeSelector,
  CustomerScannerMode,
  StaffTimeTrackingMode,
  detectQRType,
} from '@/components/kiosk';
import type { KioskMode } from '@/components/kiosk';
import { useKioskSession } from '@/hooks/kiosk';

/**
 * Unified Kiosk Page
 *
 * Combines loyalty program scanning (customer mode) with
 * employee time tracking (staff mode) on a single device.
 *
 * Features:
 * - Scanner credentials authentication (reuses existing system)
 * - Mode switching between Customer and Staff
 * - Auto-detect QR type and route accordingly
 * - Tablet-optimized layout matching app design system
 */
export default function KioskPage() {
  const [mode, setMode] = useState<KioskMode>('customer');
  const { isAuthenticated } = useKioskSession();

  // Listen for keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // F1 = Customer mode, F2 = Staff mode
      if (e.key === 'F1') {
        e.preventDefault();
        setMode('customer');
      } else if (e.key === 'F2') {
        e.preventDefault();
        setMode('staff');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle mode change
  const handleModeChange = useCallback((newMode: KioskMode) => {
    setMode(newMode);
  }, []);

  return (
    <KioskLayout>
      <KioskAuthGate>
        {/* Mode selector */}
        <KioskModeSelector mode={mode} onModeChange={handleModeChange} />

        {/* Content based on mode */}
        <div className="flex-1 overflow-hidden">
          {mode === 'customer' && <CustomerScannerMode />}
          {mode === 'staff' && <StaffTimeTrackingMode />}
        </div>
      </KioskAuthGate>
    </KioskLayout>
  );
}
